import { useContext, useEffect, useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useShallow } from "zustand/react/shallow";

import styles from "../../../assets/styles/componentStyles/billingStyles/billingPlanCard.module.scss";

import { useGenericStore } from "../../../store/genericStore";
import { useBillingStore } from "../../../store/billingStore";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import PlanCard from "./planCard";
import { PLANS } from "../constant.billing";
import { filterPricingPlansByTabs, BillingId, isCurrentPlanPublic } from "../utils.billing";
import { billingSubscriptionPlan, PricingPlan } from "../../../models/axiosModel";
import useGenericHook from "../../../hooks/useGenericHook";

interface PlansProps {
    selectedPlanId: BillingId | null;
    preferredPlanId?: BillingId | null;
    onSelectPlan: (planId: BillingId) => void;
}

const isCurrentActivePlan = (plan: PricingPlan, currentPlan?: billingSubscriptionPlan | null) => {
    if (!currentPlan) return false;

    return (
        plan.billing_plan_name?.toLowerCase() === currentPlan.billing_plan_name?.toLowerCase()
        && plan.billing_plan_currency?.toUpperCase() === currentPlan.billing_plan_currency?.toUpperCase()
        && plan.billing_plan_cycle_period_name?.toLowerCase() === currentPlan.billing_plan_cycle_period_name?.toLowerCase()
    );
};


const Plans = ({ selectedPlanId, preferredPlanId = null, onSelectPlan }: PlansProps) => {

    const { setFlagLoading } = useContext(UserContext) as UserContextPayload;

    const { showError } = useGenericStore(useShallow((state) => ({
        showError: state.showError,
    })));

    const { pricingPlans, getPricingPlansApi, billingCycle, billingCurrency, billingSubscriptionDetails } = useBillingStore(useShallow((state) => ({
        pricingPlans: state.pricingPlans,
        getPricingPlansApi: state.getPricingPlansApi,
        billingCycle: state.billingCycle,
        billingCurrency: state.billingCurrency,
        billingSubscriptionDetails: state.billingSubscriptionDetails,
    })));

    const { isScreenBelow1000 } = useGenericHook();

    const currentPlan = billingSubscriptionDetails?.current_plan;

    const filteredPlans = useMemo(() => {
        const plansByTabs = filterPricingPlansByTabs(pricingPlans, billingCycle, billingCurrency);

        if (currentPlan && !isCurrentPlanPublic(currentPlan)) {
            return plansByTabs.filter((plan) => plan.billing_plan_name?.toLowerCase() === PLANS.FREE);
        }

        return plansByTabs;
    }, [pricingPlans, billingCycle, billingCurrency, currentPlan]);
    
    const currentPlanName = currentPlan?.billing_plan_name || "Free";
    const hasUserActivePlan = Boolean(currentPlan?.billing_plan_name);

    // Get Pricing Plans
    const getPricingPlans = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        
        const res = await getPricingPlansApi();
        
        if (!res?.status) {
            showError(res?.error?.message || "Something went wrong");
        }

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    };

    // Effects
    useEffect(() => {
        if(!pricingPlans) {
            getPricingPlans();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!filteredPlans.length) return;

        if (!billingSubscriptionDetails && preferredPlanId == null) return;

        const isSelectedVisible = filteredPlans.some((plan) => String(plan.billing_plan_id) === String(selectedPlanId));
        if (selectedPlanId !== null && selectedPlanId !== "" && isSelectedVisible) return;

        const findById = (id?: BillingId | null) =>
            id != null && id !== "" ? filteredPlans.find((plan) => String(plan.billing_plan_id) === String(id)) : undefined;

        const findByName = (name?: string | null) =>
            name
                ? filteredPlans.find((plan) => plan.billing_plan_name?.toLowerCase() === name.toLowerCase())
                : undefined;

        const previousName = pricingPlans?.find((plan) => String(plan.billing_plan_id) === String(selectedPlanId))?.billing_plan_name;

        const nextPlan =
            findById(preferredPlanId)
            || findByName(previousName)
            || findById(currentPlan?.billing_plan_id)
            || (hasUserActivePlan ? findByName(currentPlanName) : undefined)
            || findByName(PLANS.FREE)
            || filteredPlans[0];

        if (nextPlan) onSelectPlan(nextPlan.billing_plan_id);

    }, [filteredPlans, selectedPlanId, preferredPlanId, onSelectPlan, hasUserActivePlan, currentPlanName, currentPlan, billingSubscriptionDetails, pricingPlans]);

    return (
        <Box className={styles.plansSection}>
            <Typography className={styles.plansSectionLabel}>
                Plan
            </Typography>

            <Grid container spacing={isScreenBelow1000 ? 3 : 5}>
                {filteredPlans.length > 0 ? (
                    filteredPlans.map((plan) => (
                        <Grid item xs={12} md={6} lg={4} key={plan.billing_plan_id}>
                            <PlanCard
                                plan={plan}
                                isCurrentPlan={isCurrentActivePlan(plan, currentPlan)}
                                isSelected={String(plan.billing_plan_id) === String(selectedPlanId)}
                                onSelect={onSelectPlan}
                            />
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Typography className={styles.noPlansAvailable}>
                            No plans available.
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default Plans;
